import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import CameraAltRoundedIcon from '@mui/icons-material/CameraAltRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import { Alert, Box, Button, Chip, CircularProgress, Stack, Typography } from '@mui/material';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getOrCreateLeaderboard, postLeaderboardScore } from '../api/leaderboard_api';
import {
  buildDifficultyProgression,
  consumeInitialMediaPlan,
  getNormalizedDifficulty,
  getTargetDifficulty,
  loadMediaItems,
} from '../game/mediaData';
import { createMediaPreload, getMediaPreloadKey } from '../game/mediaPreload';
import { getVideoEmbedUrl } from '../game/videoSources';
import { getSettings } from '../storage/settingsStorage';

const SWIPE_THRESHOLD = 50;
const SWIPE_EXIT_DISTANCE = 620;
const TAP_MOVE_TOLERANCE = 10;
const SESSION_SEEN_MEDIA_KEY_PREFIX = 'ai-or-not:session-seen-media';
const SESSION_GAME_STATE_KEY_PREFIX = 'ai-or-not:game-state';
const CORRECT_MESSAGES = [
  'Yeay!!',
  'Nailed it!',
  'Sharp eye!',
  'Good spot!',
  'You got it!',
  'Clean call!',
  'Nice read!',
  'Too easy!',
  'Correct!',
  'Great catch!',
  'Right on!',
  'Bullseye!',
];
const WRONG_MESSAGES = [
  'Nope',
  'Not quite',
  'Missed it',
  'Close one',
  'Tricky!',
  'Gotcha',
  'Almost',
  'Wrong call',
  'Oof',
  'Try again',
  'Sneaky one',
  'Good fake',
];
const MEDIA_PRELOAD_LOOKAHEAD = 5;
const MEDIA_PRELOAD_REQUEST_DELAY_MS = 200;

function getRandomMessage(messages) {
  return messages[Math.floor(Math.random() * messages.length)];
}

function getMediaItemsSources(mediaItems) {
  return [...new Set(mediaItems.map((mediaItem) => mediaItem?.src).filter(Boolean))];
}

function getSessionSeenMediaKey(deviceId) {
  return `${SESSION_SEEN_MEDIA_KEY_PREFIX}:${deviceId}`;
}

function getSessionGameStateKey(deviceId) {
  return `${SESSION_GAME_STATE_KEY_PREFIX}:${deviceId}`;
}

function readSessionSeenMediaIds(deviceId) {
  try {
    const storedIds = window.sessionStorage.getItem(getSessionSeenMediaKey(deviceId));
    const parsedIds = storedIds ? JSON.parse(storedIds) : [];

    return Array.isArray(parsedIds) ? new Set(parsedIds) : new Set();
  } catch {
    return new Set();
  }
}

function saveSessionSeenMediaIds(deviceId, seenMediaIds) {
  window.sessionStorage.setItem(
    getSessionSeenMediaKey(deviceId),
    JSON.stringify([...seenMediaIds])
  );
}

function clearSessionSeenMediaIds(deviceId) {
  window.sessionStorage.removeItem(getSessionSeenMediaKey(deviceId));
}

function readSessionGameState(deviceId) {
  try {
    const storedState = window.sessionStorage.getItem(getSessionGameStateKey(deviceId));
    const parsedState = storedState ? JSON.parse(storedState) : null;

    if (!parsedState || !Array.isArray(parsedState.mediaItems)) {
      return null;
    }

    return {
      playableMediaItems: Array.isArray(parsedState.playableMediaItems) ? parsedState.playableMediaItems : [],
      mediaItems: parsedState.mediaItems,
      roundIndex: Number(parsedState.roundIndex) || 0,
      score: Number(parsedState.score) || 0,
      streak: Number(parsedState.streak) || 0,
      bestStreak: Number(parsedState.bestStreak) || 0,
    };
  } catch {
    return null;
  }
}

function saveSessionGameState(deviceId, gameState) {
  window.sessionStorage.setItem(getSessionGameStateKey(deviceId), JSON.stringify(gameState));
}

function clearSessionGameState(deviceId) {
  window.sessionStorage.removeItem(getSessionGameStateKey(deviceId));
}

function markMediaSeenForSession(deviceId, mediaId) {
  if (!mediaId) {
    return;
  }

  const seenMediaIds = readSessionSeenMediaIds(deviceId);

  if (seenMediaIds.has(mediaId)) {
    return;
  }

  seenMediaIds.add(mediaId);
  saveSessionSeenMediaIds(deviceId, seenMediaIds);
}

function getDifficultyClassName(difficulty) {
  return `difficulty-hud difficulty-${String(difficulty).toLowerCase()}`;
}

function getLeaderboardSortValue(entry) {
  return {
    winStream: Number(entry.winStream ?? entry.bestStreak ?? entry.streak) || 0,
    accuracy: Number(entry.accuracy) || 0,
    correct: Number(entry.correct ?? entry.score) || 0,
    latestPlayedAt: entry.latestPlayedAt || entry.playedAt || '',
  };
}

function sortLeaderboardEntries(a, b) {
  const first = getLeaderboardSortValue(a);
  const second = getLeaderboardSortValue(b);

  if (second.winStream !== first.winStream) {
    return second.winStream - first.winStream;
  }

  if (second.accuracy !== first.accuracy) {
    return second.accuracy - first.accuracy;
  }

  if (second.correct !== first.correct) {
    return second.correct - first.correct;
  }

  return new Date(second.latestPlayedAt).getTime() - new Date(first.latestPlayedAt).getTime();
}

function getPlayerRank(apiEntries, deviceId, stats) {
  const currentPlayerEntry = {
    id: deviceId,
    deviceId,
    correct: stats.correct,
    total: stats.total,
    winStream: stats.winStream,
    accuracy: stats.accuracy,
    latestPlayedAt: new Date().toISOString(),
  };
  const entries = [
    ...apiEntries.filter((entry) => (entry.deviceId || entry.id) !== deviceId),
    currentPlayerEntry,
  ].sort(sortLeaderboardEntries);

  return entries.findIndex((entry) => (entry.deviceId || entry.id) === deviceId) + 1;
}

function getDeviceLeaderboardStats(leaderboard, deviceId) {
  const entries = Array.isArray(leaderboard?.entries) ? leaderboard.entries : [];
  const deviceEntry = entries.find((entry) => (entry.deviceId || entry.id) === deviceId) || {};
  const correct = Number(deviceEntry.correct ?? deviceEntry.score) || 0;
  const total = Number(deviceEntry.total) || 0;

  return {
    correct,
    total,
    winStream: Number(deviceEntry.winStream ?? deviceEntry.bestStreak ?? deviceEntry.streak) || 0,
    accuracy: Number.isFinite(Number(deviceEntry.accuracy))
      ? Number(deviceEntry.accuracy)
      : total > 0
        ? correct / total
        : 0,
  };
}

function GameScreen({ deviceId, playerName, onNavigate, onShowFeedback }) {
  const [playableMediaItems, setPlayableMediaItems] = useState([]);
  const [mediaItems, setMediaItems] = useState([]);
  const [loadingState, setLoadingState] = useState('loading');
  const [loadError, setLoadError] = useState('');
  const [roundIndex, setRoundIndex] = useState(0);
  const [score, setScore] = useState(0);
  const [streak, setStreak] = useState(0);
  const [bestStreak, setBestStreak] = useState(0);
  const [feedback, setFeedback] = useState(null);
  const [mediaError, setMediaError] = useState(false);
  const [mediaBufferItems, setMediaBufferItems] = useState([]);
  const [dragOffset, setDragOffset] = useState(0);
  const [swipePhase, setSwipePhase] = useState('idle');
  const [cardHidden, setCardHidden] = useState(false);
  const [leaderboardSummary, setLeaderboardSummary] = useState({
    globalRank: null,
    maxStreak: 0,
    loading: true,
  });
  const [leaderboardStats, setLeaderboardStats] = useState({
    correct: 0,
    total: 0,
    winStream: 0,
    accuracy: 0,
  });
  const dragStartX = useRef(null);
  const nativeVideoRef = useRef(null);
  const mediaPreloadCacheRef = useRef(new Map());
  const mediaBufferItemsRef = useRef([]);
  const easyRestartMediaPlanRef = useRef([]);
  const shouldLogMediaBufferRef = useRef(false);
  const mediaBufferTimeoutsRef = useRef([]);
  const settings = useMemo(() => getSettings(), []);

  const currentMedia = mediaItems[roundIndex];
  const videoEmbedUrl =
    currentMedia?.type === 'video' ? getVideoEmbedUrl(currentMedia.src, settings.autoplayVideo) : '';
  const isVideoMedia = currentMedia?.type === 'video';
  const isNativeVideoMedia = isVideoMedia && !videoEmbedUrl;
  const nativeVideoBufferItems = mediaBufferItems.filter(
    (mediaItem) =>
      mediaItem?.type === 'video' && !getVideoEmbedUrl(mediaItem.src, settings.autoplayVideo)
  );
  const nativeVideoRenderItems =
    isNativeVideoMedia &&
    !nativeVideoBufferItems.some(
      (mediaItem) => getMediaPreloadKey(mediaItem) === getMediaPreloadKey(currentMedia)
    )
      ? [currentMedia, ...nativeVideoBufferItems]
      : nativeVideoBufferItems;

  const refreshLeaderboardSummary = useCallback(() => {
    setLeaderboardSummary((summary) => ({
      ...summary,
      loading: true,
    }));

    getOrCreateLeaderboard({ deviceId, playerName })
      .then((leaderboard) => {
        const apiEntries = Array.isArray(leaderboard?.entries) ? leaderboard.entries : [];
        const stats = getDeviceLeaderboardStats(leaderboard, deviceId);

        setLeaderboardStats(stats);
        setLeaderboardSummary({
          globalRank: leaderboard?.deviceRank || getPlayerRank(apiEntries, deviceId, stats),
          maxStreak: stats.winStream,
          loading: false,
        });
      })
      .catch(() => {
        setLeaderboardSummary((summary) => ({
          ...summary,
          globalRank: null,
          loading: false,
        }));
      });
  }, [deviceId, playerName]);

  const loadGame = useCallback(async ({ resetSeenMedia = false } = {}) => {
    setLoadingState('loading');
    setLoadError('');
    setFeedback(null);

    try {
      if (resetSeenMedia) {
        clearSessionSeenMediaIds(deviceId);
        clearSessionGameState(deviceId);
      }

      const savedGameState = resetSeenMedia ? null : readSessionGameState(deviceId);

      if (savedGameState) {
        setPlayableMediaItems(savedGameState.playableMediaItems);
        setMediaItems(savedGameState.mediaItems);
        setRoundIndex(savedGameState.roundIndex);
        setScore(savedGameState.score);
        setStreak(savedGameState.streak);
        setBestStreak(savedGameState.bestStreak);
        setLoadingState('ready');
        return;
      }

      const seenMediaIds = readSessionSeenMediaIds(deviceId);
      const preparedMediaPlan = consumeInitialMediaPlan(deviceId, seenMediaIds, 0);
      const playableMediaItems =
        preparedMediaPlan?.playableMediaItems ||
        (await loadMediaItems()).filter((mediaItem) => !seenMediaIds.has(mediaItem.id));
      const nextMediaItems = preparedMediaPlan?.mediaItems || buildDifficultyProgression(playableMediaItems, 0);

      setPlayableMediaItems(playableMediaItems);
      setMediaItems(nextMediaItems);
      setRoundIndex(0);
      setScore(0);
      setStreak(0);
      setBestStreak(0);
      setLoadingState('ready');
    } catch (error) {
      setLoadError(error.message);
      setLoadingState('error');
    }
  }, [deviceId]);

  useEffect(() => {
    loadGame();
  }, [loadGame]);

  useEffect(() => {
    const preloadCache = mediaPreloadCacheRef.current;
    const bufferTimeouts = mediaBufferTimeoutsRef.current;

    return () => {
      bufferTimeouts.forEach(window.clearTimeout);
      preloadCache.forEach((preload) => preload.dispose());
      preloadCache.clear();
    };
  }, []);

  useEffect(() => {
    mediaBufferItemsRef.current = mediaBufferItems;
  }, [mediaBufferItems]);

  useEffect(() => {
    if (loadingState !== 'ready') {
      return;
    }

    saveSessionGameState(deviceId, {
      playableMediaItems,
      mediaItems,
      roundIndex,
      score,
      streak,
      bestStreak,
    });
  }, [bestStreak, deviceId, loadingState, mediaItems, playableMediaItems, roundIndex, score, streak]);

  useEffect(() => {
    refreshLeaderboardSummary();
  }, [refreshLeaderboardSummary]);

  useEffect(() => {
    setMediaError(false);
  }, [currentMedia?.id]);

  useEffect(() => {
    markMediaSeenForSession(deviceId, currentMedia?.id);
  }, [currentMedia?.id, deviceId]);

  useEffect(() => {
    if (loadingState !== 'ready' || !currentMedia) {
      return;
    }

    const currentKey = getMediaPreloadKey(currentMedia);
    const preloadCandidates = mediaItems
      .slice(roundIndex + 1, roundIndex + 1 + MEDIA_PRELOAD_LOOKAHEAD)
      .filter((mediaItem) => mediaItem?.src);
    const seenMediaIds = readSessionSeenMediaIds(deviceId);
    const unavailableRestartMediaIds = new Set(
      [currentMedia.id, ...seenMediaIds].filter(Boolean)
    );
    const easyRestartMediaItems = easyRestartMediaPlanRef.current.filter(
      (mediaItem) => mediaItem?.src && !unavailableRestartMediaIds.has(mediaItem.id)
    );

    if (easyRestartMediaItems.length < MEDIA_PRELOAD_LOOKAHEAD) {
      const easyRestartMediaKeys = new Set(easyRestartMediaItems.map(getMediaPreloadKey));
      const easyRestartPlayableMediaItems = playableMediaItems.filter(
        (mediaItem) =>
          mediaItem?.src &&
          !unavailableRestartMediaIds.has(mediaItem.id) &&
          !easyRestartMediaKeys.has(getMediaPreloadKey(mediaItem))
      );
      const topUpMediaItems = buildDifficultyProgression(easyRestartPlayableMediaItems, 0).slice(
        0,
        MEDIA_PRELOAD_LOOKAHEAD - easyRestartMediaItems.length
      );

      easyRestartMediaItems.push(...topUpMediaItems);
    }

    easyRestartMediaPlanRef.current = easyRestartMediaItems;
    const easyRestartPreloadCandidates = easyRestartMediaItems.slice(0, MEDIA_PRELOAD_LOOKAHEAD);
    const allPreloadCandidates = [...preloadCandidates, ...easyRestartPreloadCandidates];
    const bufferItems = [currentMedia, ...allPreloadCandidates].filter((mediaItem, index, items) => {
      const preloadKey = getMediaPreloadKey(mediaItem);

      return preloadKey && items.findIndex((item) => getMediaPreloadKey(item) === preloadKey) === index;
    });
    const preloadableCandidates = allPreloadCandidates.filter(
      (mediaItem) =>
        mediaItem.type !== 'video' || getVideoEmbedUrl(mediaItem.src, settings.autoplayVideo)
    );
    const retainedKeys = new Set([
      currentMedia.type === 'video' && !videoEmbedUrl ? '' : currentKey,
      ...preloadableCandidates.map(getMediaPreloadKey),
    ]);

    mediaBufferTimeoutsRef.current.forEach(window.clearTimeout);
    mediaBufferTimeoutsRef.current = [];

    const currentBufferKeys = new Set(mediaBufferItemsRef.current.map(getMediaPreloadKey));
    const retainedBufferItems = bufferItems.filter((mediaItem, index) => {
      const preloadKey = getMediaPreloadKey(mediaItem);

      return index === 0 || currentBufferKeys.has(preloadKey);
    });
    const retainedBufferKeys = new Set(retainedBufferItems.map(getMediaPreloadKey));
    const missingBufferItems = bufferItems.filter(
      (mediaItem) => !retainedBufferKeys.has(getMediaPreloadKey(mediaItem))
    );
    const preloadableKeys = new Set(preloadableCandidates.map(getMediaPreloadKey));

    mediaBufferItemsRef.current = retainedBufferItems;
    setMediaBufferItems(retainedBufferItems);

    missingBufferItems.forEach((mediaItem, index) => {
      const timeoutId = window.setTimeout(() => {
        const preloadKey = getMediaPreloadKey(mediaItem);

        if (preloadableKeys.has(preloadKey) && !mediaPreloadCacheRef.current.has(preloadKey)) {
          const preload = createMediaPreload(mediaItem);

          if (preload) {
            mediaPreloadCacheRef.current.set(preloadKey, preload);
          }
        }

        setMediaBufferItems((bufferedItems) => {
          if (bufferedItems.some((bufferedItem) => getMediaPreloadKey(bufferedItem) === preloadKey)) {
            return bufferedItems;
          }

          const nextBufferedItems = [...bufferedItems, mediaItem];
          mediaBufferItemsRef.current = nextBufferedItems;

          return nextBufferedItems;
        });
      }, (index + 1) * MEDIA_PRELOAD_REQUEST_DELAY_MS);

      mediaBufferTimeoutsRef.current.push(timeoutId);
    });

    mediaPreloadCacheRef.current.forEach((preload, preloadKey) => {
      if (retainedKeys.has(preloadKey)) {
        return;
      }

      preload.dispose();
      mediaPreloadCacheRef.current.delete(preloadKey);
    });

    if (shouldLogMediaBufferRef.current) {
      shouldLogMediaBufferRef.current = false;
      console.log(JSON.stringify(getMediaItemsSources(bufferItems)));
    }
  }, [currentMedia, deviceId, loadingState, mediaItems, playableMediaItems, roundIndex, settings, videoEmbedUrl]);

  useEffect(() => {
    if (loadingState !== 'ready' || !currentMedia) {
      return;
    }

    const targetDifficulty = getTargetDifficulty(score);

    if (getNormalizedDifficulty(currentMedia.difficulty) === targetDifficulty) {
      return;
    }

    const seenMediaIds = readSessionSeenMediaIds(deviceId);
    const nextPlayableMediaItems = playableMediaItems.filter(
      (mediaItem) => !seenMediaIds.has(mediaItem.id)
    );

    setPlayableMediaItems(nextPlayableMediaItems);
    setMediaItems(buildDifficultyProgression(nextPlayableMediaItems, score));
    setRoundIndex(0);
  }, [currentMedia, deviceId, loadingState, playableMediaItems, score]);

  const handleAnswer = (answer) => {
    if (!currentMedia || feedback || leaderboardSummary.loading) {
      return;
    }

    shouldLogMediaBufferRef.current = true;
    markMediaSeenForSession(deviceId, currentMedia.id);

    const isCorrect = answer === currentMedia.answer;
    const nextScore = isCorrect ? score + 1 : 0;
    const nextStreak = isCorrect ? streak + 1 : 0;
    const nextBestStreak = Math.max(bestStreak, nextStreak);

    if (settings.haptics && window.navigator.vibrate) {
      window.navigator.vibrate(isCorrect ? 35 : [20, 30, 20]);
    }

    setScore(nextScore);
    setStreak(nextStreak);
    setBestStreak(nextBestStreak);

    const nextLeaderboardStats = {
      correct: leaderboardStats.correct + (isCorrect ? 1 : 0),
      total: leaderboardStats.total + 1,
      winStream: Math.max(leaderboardStats.winStream, nextStreak),
    };

    nextLeaderboardStats.accuracy =
      nextLeaderboardStats.total > 0 ? nextLeaderboardStats.correct / nextLeaderboardStats.total : 0;

    setLeaderboardStats(nextLeaderboardStats);
    setLeaderboardSummary((summary) => ({
      ...summary,
      maxStreak: nextLeaderboardStats.winStream,
    }));

    postLeaderboardScore({
      deviceId,
      playerName,
      score: nextLeaderboardStats.correct,
      correct: nextLeaderboardStats.correct,
      total: nextLeaderboardStats.total,
      winStream: nextLeaderboardStats.winStream,
    })
      .then(refreshLeaderboardSummary)
      .catch(() => {
        setLeaderboardSummary((summary) => ({
          ...summary,
          loading: false,
        }));
      });

    if (isCorrect) {
      const nextRoundIndex = roundIndex + 1;

      if (nextRoundIndex >= mediaItems.length) {
        const seenMediaIds = readSessionSeenMediaIds(deviceId);
        const nextPlayableMediaItems = playableMediaItems.filter(
          (mediaItem) => mediaItem.id !== currentMedia.id && !seenMediaIds.has(mediaItem.id)
        );

        if (nextPlayableMediaItems.length > 0) {
          setPlayableMediaItems(nextPlayableMediaItems);
          setMediaItems(buildDifficultyProgression(nextPlayableMediaItems, nextScore));
          setRoundIndex(0);
        } else {
          setRoundIndex(nextRoundIndex);
        }
      } else {
        const nextDifficulty = getTargetDifficulty(nextScore);

        if (getNormalizedDifficulty(mediaItems[nextRoundIndex]?.difficulty) === nextDifficulty) {
          setRoundIndex(nextRoundIndex);
        } else {
          const seenMediaIds = readSessionSeenMediaIds(deviceId);
          const nextPlayableMediaItems = playableMediaItems.filter(
            (mediaItem) => mediaItem.id !== currentMedia.id && !seenMediaIds.has(mediaItem.id)
          );

          setPlayableMediaItems(nextPlayableMediaItems);
          setMediaItems(buildDifficultyProgression(nextPlayableMediaItems, nextScore));
          setRoundIndex(0);
        }
      }
    } else {
      const seenMediaIds = readSessionSeenMediaIds(deviceId);
      const nextPlayableMediaItems = playableMediaItems.filter(
        (mediaItem) => !seenMediaIds.has(mediaItem.id)
      );

      setPlayableMediaItems(nextPlayableMediaItems);

      if (nextPlayableMediaItems.length > 0) {
        const nextPlayableMediaKeys = new Set(nextPlayableMediaItems.map(getMediaPreloadKey));
        const easyRestartMediaItems = easyRestartMediaPlanRef.current.filter((mediaItem) =>
          nextPlayableMediaKeys.has(getMediaPreloadKey(mediaItem))
        );
        const easyRestartMediaKeys = new Set(easyRestartMediaItems.map(getMediaPreloadKey));
        const remainingRestartMediaItems = nextPlayableMediaItems.filter(
          (mediaItem) => !easyRestartMediaKeys.has(getMediaPreloadKey(mediaItem))
        );

        setMediaItems(
          easyRestartMediaItems.length > 0
            ? [
                ...easyRestartMediaItems,
                ...buildDifficultyProgression(remainingRestartMediaItems, nextScore),
              ]
            : buildDifficultyProgression(nextPlayableMediaItems, nextScore)
        );
        setRoundIndex(0);
      } else {
        setRoundIndex(mediaItems.length);
      }
    }

    const nextFeedback = {
      isCorrect,
      title: getRandomMessage(isCorrect ? CORRECT_MESSAGES : WRONG_MESSAGES),
      answerLabel: currentMedia.answer === 'ai' ? 'AI' : 'Real',
      onClose: handleCloseFeedback,
    };

    setFeedback(nextFeedback);
    onShowFeedback(nextFeedback);
    setSwipePhase('idle');
    setDragOffset(0);
  };

  const submitWithSwipeAnimation = (answer) => {
    if (feedback || swipePhase === 'exiting' || leaderboardSummary.loading) {
      return;
    }

    const direction = answer === 'ai' ? 'left' : 'right';

    setSwipePhase('exiting');
    setDragOffset(direction === 'left' ? -SWIPE_EXIT_DISTANCE : SWIPE_EXIT_DISTANCE);

    window.setTimeout(() => {
      handleAnswer(answer);
    }, 260);
  };

  const handleCloseFeedback = () => {
    setFeedback(null);
    setSwipePhase('idle');
    setCardHidden(false);
    setDragOffset(0);
  };

  const handlePointerDown = (event) => {
    if (feedback || swipePhase === 'exiting' || leaderboardSummary.loading) {
      return;
    }

    dragStartX.current = event.clientX;
    event.currentTarget.setPointerCapture(event.pointerId);
    setSwipePhase('dragging');
  };

  const handlePointerMove = (event) => {
    if (dragStartX.current === null) {
      return;
    }

    setDragOffset(event.clientX - dragStartX.current);
  };

  const toggleNativeVideoPlayback = () => {
    const videoElement = nativeVideoRef.current;

    if (!videoElement) {
      return;
    }

    if (videoElement.paused) {
      videoElement.play().catch(() => {});
    } else {
      videoElement.pause();
    }
  };

  const handlePointerUp = (event) => {
    const nextDragOffset =
      dragStartX.current === null ? dragOffset : event.clientX - dragStartX.current;

    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }

    if (nextDragOffset <= -SWIPE_THRESHOLD) {
      submitWithSwipeAnimation('ai');
      dragStartX.current = null;
      return;
    }

    if (nextDragOffset >= SWIPE_THRESHOLD) {
      submitWithSwipeAnimation('not-ai');
      dragStartX.current = null;
      return;
    }

    if (isNativeVideoMedia && Math.abs(nextDragOffset) <= TAP_MOVE_TOLERANCE) {
      toggleNativeVideoPlayback();
    }

    dragStartX.current = null;
    setSwipePhase('idle');
    setDragOffset(0);
  };

  const handlePointerCancel = (event) => {
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }

    dragStartX.current = null;
    setSwipePhase('idle');
    setDragOffset(0);
  };

  if (loadingState === 'loading') {
    return (
      <Box className="screen">
        <Stack spacing={2} alignItems="center">
          <CircularProgress />
          <Typography color="text.secondary">Loading media</Typography>
        </Stack>
      </Box>
    );
  }

  if (loadingState === 'error') {
    return (
      <Box className="screen">
        <Stack spacing={2} sx={{ width: '100%' }}>
          <Typography variant="h2">Media failed to load</Typography>
          <Alert severity="error">{loadError}</Alert>
          <Button variant="contained" startIcon={<RefreshRoundedIcon />} onClick={loadGame}>
            Retry
          </Button>
        </Stack>
      </Box>
    );
  }

  if (!currentMedia) {
    const hasCompletedSession =
      (mediaItems.length > 0 && roundIndex >= mediaItems.length) ||
      (mediaItems.length === 0 && playableMediaItems.length === 0 && readSessionSeenMediaIds(deviceId).size > 0);

    return (
      <Box className="screen">
        <Stack spacing={2} sx={{ width: '100%' }}>
          <Typography variant="h2">{hasCompletedSession ? 'Session complete' : 'No playable media'}</Typography>
          <Typography color="text.secondary">
            {hasCompletedSession
              ? 'You have seen every media item available for this browser session.'
              : 'Add valid image or video entries to `public/media.json` to start a game.'}
          </Typography>
          <Button
            variant="contained"
            startIcon={<RefreshRoundedIcon />}
            onClick={() => loadGame({ resetSeenMedia: hasCompletedSession })}
          >
            {hasCompletedSession ? 'Start Fresh Session' : 'Reload Media'}
          </Button>
        </Stack>
      </Box>
    );
  }

  return (
    <Box className="screen game-screen">
      <Stack spacing={2} sx={{ width: '100%' }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
          <Box sx={{ width: '100%' }}>
            <Typography variant="overline" color="text.secondary">
              IS THIS VIDEO
            </Typography>
            <Typography variant="h2" >Real or AI</Typography>
          </Box>
          <Stack direction="row" spacing={1} alignItems="stretch">
            {currentMedia.difficulty && (
              <Box className={getDifficultyClassName(currentMedia.difficulty)}>
                <Typography className="difficulty-value">{currentMedia.difficulty}</Typography>
              </Box>
            )}
            <Box className="streak-hud">
              <Typography className="streak-number">{streak}</Typography>
            </Box>
          </Stack>
        </Stack>

        <Stack direction="row" spacing={1} className="game-stats-strip">
          <Box
            component="button"
            type="button"
            className="game-stat-pill game-stat-button"
            onClick={() => onNavigate('/scores')}
          >
            <Typography className="game-stat-value">
              {leaderboardSummary.loading ? '--' : leaderboardSummary.globalRank ? `#${leaderboardSummary.globalRank}` : '--'}
            </Typography>
            <Typography className="game-stat-label">Global Rank</Typography>
          </Box>
          <Box className="game-stat-pill">
            <Typography className="game-stat-value">{Math.max(leaderboardSummary.maxStreak, bestStreak)}</Typography>
            <Typography className="game-stat-label">Max Streak</Typography>
          </Box>
        </Stack>

        <Box className="game-separator" />

        <Box
          className={[
            'media-stage',
            feedback ? (feedback.isCorrect ? 'is-correct' : 'is-wrong') : '',
            swipePhase === 'dragging' ? 'is-dragging' : '',
            swipePhase === 'exiting' ? 'is-exiting' : '',
            cardHidden ? 'is-hidden' : '',
          ]
            .filter(Boolean)
            .join(' ')}
          sx={{ transform: `translateX(${dragOffset}px) rotate(${dragOffset / 28}deg)` }}
        >
          <Box className={`swipe-stamp swipe-stamp-ai ${dragOffset < -18 ? 'is-visible' : ''}`}>AI</Box>
          <Box className={`swipe-stamp swipe-stamp-not ${dragOffset > 18 ? 'is-visible' : ''}`}>Real</Box>

          <Chip className="media-chip" label={currentMedia.type} size="small" />

          {currentMedia.type === 'image' && !mediaError && (
            <Box
              component="img"
              src={currentMedia.src}
              alt={currentMedia.category || 'Game media'}
              className="media-asset"
              draggable={false}
              onError={() => setMediaError(true)}
            />
          )}

          {currentMedia.type === 'video' && videoEmbedUrl && !mediaError && (
            <Box
              component="iframe"
              src={videoEmbedUrl}
              title={currentMedia.category || currentMedia.id}
              className="media-asset"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
              onError={() => setMediaError(true)}
            />
          )}

          {isNativeVideoMedia &&
            !mediaError &&
            nativeVideoRenderItems.map((mediaItem) => {
              const isActiveVideo = getMediaPreloadKey(mediaItem) === getMediaPreloadKey(currentMedia);

              return (
                <Box
                  component="video"
                  key={getMediaPreloadKey(mediaItem)}
                  ref={isActiveVideo ? nativeVideoRef : null}
                  src={mediaItem.src}
                  className={isActiveVideo ? 'media-asset' : undefined}
                  autoPlay={isActiveVideo && settings.autoplayVideo}
                  controls={isActiveVideo}
                  muted
                  playsInline
                  preload="auto"
                  sx={
                    isActiveVideo
                      ? undefined
                      : {
                          position: 'fixed',
                          width: 1,
                          height: 1,
                          opacity: 0,
                          pointerEvents: 'none',
                          left: -9999,
                          top: -9999,
                        }
                  }
                  onError={isActiveVideo ? () => setMediaError(true) : undefined}
                />
              );
            })}

          {mediaError && (
            <Stack spacing={1} className="media-caption">
              <Typography variant="h2">Media unavailable</Typography>
              <Typography color="text.secondary">Check the source URL in `public/media.json`.</Typography>
            </Stack>
          )}

          {!feedback && (
            isVideoMedia && !isNativeVideoMedia ? (
              <>
                <Box
                  className="swipe-capture swipe-capture-video-edge swipe-capture-video-left"
                  onPointerDown={handlePointerDown}
                  onPointerMove={handlePointerMove}
                  onPointerUp={handlePointerUp}
                  onPointerCancel={handlePointerCancel}
                />
                <Box
                  className="swipe-capture swipe-capture-video-edge swipe-capture-video-right"
                  onPointerDown={handlePointerDown}
                  onPointerMove={handlePointerMove}
                  onPointerUp={handlePointerUp}
                  onPointerCancel={handlePointerCancel}
                />
              </>
            ) : (
              <Box
                className="swipe-capture"
                onPointerDown={handlePointerDown}
                onPointerMove={handlePointerMove}
                onPointerUp={handlePointerUp}
                onPointerCancel={handlePointerCancel}
              />
            )
          )}

        </Box>

        <Stack direction="row" spacing={1.25} className="answer-actions">
          <Button
            fullWidth
            variant="contained"
            color="error"
            size="large"
            startIcon={<AutoAwesomeRoundedIcon />}
            onClick={() => submitWithSwipeAnimation('ai')}
            disabled={leaderboardSummary.loading}
          >
            AI
          </Button>
          <Button
            fullWidth
            variant="contained"
            color="primary"
            size="large"
            startIcon={<CameraAltRoundedIcon />}
            onClick={() => submitWithSwipeAnimation('not-ai')}
            disabled={leaderboardSummary.loading}
          >
            Real
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
}

export default GameScreen;
